import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Clock, ChefHat, Package, Truck, ArrowLeft, Loader2, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useLanguage } from '@/context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUSES = ['nuevo', 'aceptado', 'preparando', 'listo', 'entregado'];

const OrderTrackingPage = () => {
  const { orderId } = useParams();
  const { t, lang } = useLanguage();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const fetchOrder = useCallback(async () => {
    if (!orderId) return;
    try {
      const res = await axios.get(`${API}/orders/${orderId}`);
      setOrder(res.data);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(fetchOrder, 5000);
    return () => clearInterval(interval);
  }, [fetchOrder]);

  const handleCancel = async () => {
    if (!window.confirm(lang === 'en' ? 'Are you sure you want to cancel this order?' : 'Estas seguro de que quieres cancelar este pedido?')) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/orders/${orderId}/customer-cancel`);
      toast.success(lang === 'en' ? 'Order cancelled. Refund will be processed.' : 'Pedido cancelado. El reembolso se procesara automaticamente.');
      fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusIndex = (status) => STATUSES.indexOf(status);

  const statusLabels = {
    nuevo: lang === 'en' ? 'Order Placed' : 'Pedido Realizado',
    aceptado: lang === 'en' ? 'Accepted' : 'Aceptado',
    preparando: lang === 'en' ? 'Preparing' : 'Preparando',
    listo: lang === 'en' ? 'Ready' : 'Listo',
    entregado: lang === 'en' ? 'Delivered' : 'Entregado'
  };

  const statusIcons = {
    nuevo: Clock,
    aceptado: CheckCircle,
    preparando: ChefHat,
    listo: Package,
    entregado: Truck
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#C44536] animate-spin" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <div className="text-center">
          <Package className="w-16 h-16 text-[#E8DEC8] mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#2D231E] mb-2">
            {lang === 'en' ? 'Order not found' : 'Pedido no encontrado'}
          </h1>
          <Link to="/">
            <Button className="btn-primary mt-4" data-testid="back-home-btn">{t('order_back')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentIdx = getStatusIndex(order.status);
  const isCancelled = order.status === 'cancelado';

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="order-tracking-page">
      <header className="sticky top-0 z-50 glass border-b border-[#2D231E]/10">
        <div className="container-max flex items-center h-16">
          <Link to="/" className="flex items-center gap-2 text-[#5C4D43] hover:text-[#C44536] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-sans text-sm font-medium">{t('order_back')}</span>
          </Link>
          <h1 className="font-serif text-xl font-bold text-[#2D231E] mx-auto">{t('order_title')}</h1>
        </div>
      </header>

      <div className="container-max py-8 max-w-2xl">
        {/* Order ID & Time */}
        <div className="text-center mb-8">
          <p className="overline mb-2">{t('order_number')} #{orderId.substring(0, 8).toUpperCase()}</p>
          <p className="font-sans text-sm text-[#5C4D43]">
            {new Date(order.created_at).toLocaleString(lang === 'en' ? 'en-GB' : 'es-ES')}
          </p>
          {order.estimated_time && !isCancelled && (
            <div className="mt-3 inline-flex items-center gap-2 bg-[#2C5E3E]/10 text-[#2C5E3E] px-4 py-2 rounded-full">
              <Clock className="w-4 h-4" />
              <span className="font-sans text-sm font-medium">{t('order_est_time')}: {order.estimated_time}</span>
            </div>
          )}
        </div>

        {/* Cancelled Banner */}
        {isCancelled && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8 text-center" data-testid="order-cancelled-banner">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="font-serif text-xl font-semibold text-red-700 mb-2">
              {lang === 'en' ? 'Order Cancelled' : 'Pedido Cancelado'}
            </h2>
            {order.cancel_reason && <p className="font-sans text-sm text-red-600 mb-2">{order.cancel_reason}</p>}
            {order.payment_status === 'reembolsado' && (
              <p className="font-sans text-sm text-[#2C5E3E] font-medium">
                {lang === 'en' ? 'Refund processed. It may take 5-10 business days.' : 'Reembolso procesado. Puede tardar 5-10 dias habiles.'}
              </p>
            )}
          </div>
        )}

        {/* Cancel Button for Customer */}
        {order.can_cancel && !isCancelled && (
          <div className="bg-[#E28743]/10 border border-[#E28743]/30 rounded-2xl p-4 mb-6 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="cancel-order-section">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#E28743]" />
              <span className="font-sans text-sm text-[#2D231E]">
                {lang === 'en' 
                  ? `You can cancel within ${Math.floor(order.cancel_seconds_left / 60)}:${String(order.cancel_seconds_left % 60).padStart(2, '0')} minutes`
                  : `Puedes cancelar en ${Math.floor(order.cancel_seconds_left / 60)}:${String(order.cancel_seconds_left % 60).padStart(2, '0')} minutos`
                }
              </span>
            </div>
            <Button
              onClick={handleCancel}
              disabled={cancelling}
              variant="destructive"
              size="sm"
              className="rounded-full"
              data-testid="customer-cancel-btn"
            >
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
              {lang === 'en' ? 'Cancel Order' : 'Cancelar Pedido'}
            </Button>
          </div>
        )}

        {/* Status Progress */}
        {!isCancelled && (
          <div className="bg-[#F5EFE6] rounded-2xl p-6 md:p-8 border border-[#E8DEC8] mb-8" data-testid="order-status-tracker">
            <h2 className="font-serif text-lg font-semibold text-[#2D231E] mb-6">{t('order_status')}</h2>
            <div className="space-y-0">
              {STATUSES.map((status, idx) => {
                const Icon = statusIcons[status];
                const isActive = idx <= currentIdx;
                const isCurrent = idx === currentIdx;
                return (
                  <div key={status} className="flex items-start gap-4" data-testid={`status-step-${status}`}>
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                        isCurrent ? 'bg-[#C44536] text-white ring-4 ring-[#C44536]/20' :
                        isActive ? 'bg-[#2C5E3E] text-white' : 'bg-[#E8DEC8] text-[#5C4D43]'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      {idx < STATUSES.length - 1 && (
                        <div className={`w-0.5 h-10 ${isActive ? 'bg-[#2C5E3E]' : 'bg-[#E8DEC8]'}`} />
                      )}
                    </div>
                    <div className="pt-2">
                      <p className={`font-sans font-medium ${isCurrent ? 'text-[#C44536]' : isActive ? 'text-[#2D231E]' : 'text-[#5C4D43]/50'}`}>
                        {statusLabels[status]}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Order Items */}
        <div className="bg-[#F5EFE6] rounded-2xl p-6 border border-[#E8DEC8]">
          <h2 className="font-serif text-lg font-semibold text-[#2D231E] mb-4">{t('order_items')}</h2>
          <div className="space-y-3">
            {order.items?.map((item, idx) => (
              <div key={idx} className="flex justify-between items-center">
                <div>
                  <span className="font-sans text-sm text-[#2D231E]">{item.name}</span>
                  <span className="font-sans text-xs text-[#5C4D43] ml-2">x{item.quantity}</span>
                  {item.spiciness && <span className="font-sans text-xs text-[#E28743] ml-2">({item.spiciness})</span>}
                </div>
                <span className="font-sans text-sm font-medium text-[#2D231E]">
                  {(item.price * item.quantity).toFixed(2)} EUR
                </span>
              </div>
            ))}
          </div>
          <Separator className="my-4 bg-[#E8DEC8]" />
          <div className="flex justify-between items-center mb-1">
            <span className="font-sans text-[#5C4D43]">{t('checkout_subtotal')}</span>
            <span className="font-sans text-[#2D231E]">{(order.subtotal || order.total).toFixed(2)} EUR</span>
          </div>
          {order.delivery_fee > 0 && (
            <div className="flex justify-between items-center mb-1">
              <span className="font-sans text-[#5C4D43]">{t('checkout_delivery_fee')}</span>
              <span className="font-sans text-[#2D231E]">{order.delivery_fee.toFixed(2)} EUR</span>
            </div>
          )}
          <div className="flex justify-between items-center mt-2">
            <span className="font-serif text-lg font-semibold text-[#2D231E]">{t('checkout_total')}</span>
            <span className="font-serif text-2xl font-bold text-[#C44536]">{order.total.toFixed(2)} EUR</span>
          </div>
        </div>

        {/* Contact info for cancelled or non-cancellable */}
        {!order.can_cancel && !isCancelled && order.status !== 'entregado' && (
          <div className="mt-6 text-center">
            <p className="font-sans text-sm text-[#5C4D43]">
              {lang === 'en' 
                ? 'Need to cancel? Contact us: +34 602 704 745'
                : 'Necesitas cancelar? Contactanos: +34 602 704 745'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTrackingPage;
